const OEMBED_ENDPOINT = 'https://vimeo.com/api/oembed.json';

/**
 * Vimeo stills are only reachable at runtime, and the poster is rendered up to three times per page
 * (gallery slide, desktop thumbnail strip, mobile thumbnail strip) plus once more after every
 * variant change, when media-gallery.js swaps in freshly rendered markup. Cache per video id so all
 * of those share a single request.
 *
 * @type {Map<string, Promise<string | null>>}
 */
const posterCache = new Map();

/**
 * Looks up the still for a Vimeo video.
 * @param {string} id - The numeric Vimeo video id.
 * @returns {Promise<string | null>} The thumbnail URL, or null when it can't be resolved.
 */
function fetchVimeoPoster(id) {
  const cached = posterCache.get(id);
  if (cached) return cached;

  const source = encodeURIComponent(`https://vimeo.com/${id}`);
  const request = fetch(`${OEMBED_ENDPOINT}?url=${source}&width=1280`)
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => data?.thumbnail_url ?? null)
    .catch(() => null);

  posterCache.set(id, request);

  return request;
}

/**
 * Fills in the poster image for a metafield-driven product video.
 *
 * A YouTube still is resolved in Liquid, so this only has work to do for Vimeo. Until an image
 * lands — and permanently, if the lookup fails — the element stays flagged so CSS can render a
 * neutral tile instead of a broken image.
 *
 * @extends {HTMLElement}
 */
class VideoPoster extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    if (this.dataset.videoType !== 'vimeo') return;

    const id = this.dataset.videoId;
    const image = this.querySelector('img');

    if (!id || !image || image.getAttribute('src')) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    fetchVimeoPoster(id).then((src) => {
      if (signal.aborted) return;

      if (!src) {
        this.removeAttribute('data-pending');
        this.setAttribute('data-failed', '');
        return;
      }

      image.src = src;
      this.removeAttribute('data-pending');
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }
}

if (!customElements.get('video-poster')) {
  customElements.define('video-poster', VideoPoster);
}
